import { useQuery } from '@tanstack/react-query';
import { createCrudHooks } from '@/hooks/lib/create-crud-hooks';
import { api } from '@/lib/api';

export interface Referrer {
  id: string;
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  type: 'BROKER' | 'AGENT' | 'DIRECT' | 'INTERNAL' | 'OTHER';
  commissionRate?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { loans: number };
}

export interface ReferrerStats extends Referrer {
  totalLoansReferred: number;
  totalCommissionEarned: number;
  activeLoansCount: number;
}

// Type definitions for CRUD operations
interface CreateReferrerBody {
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  type?: string;
  commissionRate?: number;
  notes?: string;
}

interface UpdateReferrerBody {
  id: string;
  name?: string;
  company?: string;
  email?: string;
  phone?: string;
  type?: string;
  commissionRate?: number;
  notes?: string;
  isActive?: boolean;
}

// Global referrer CRUD hooks via factory
const { useDetail, useCreate, useUpdate } = createCrudHooks<
  Referrer,
  CreateReferrerBody,
  UpdateReferrerBody
>('referrers', '/referrers');

export const useReferrer = useDetail;
export const useCreateReferrer = useCreate;
export const useUpdateReferrer = useUpdate;

// Custom useReferrers with query params support (not a standard CRUD pattern)
export function useReferrers(params?: { search?: string; type?: string; isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set('search', params.search);
  if (params?.type) searchParams.set('type', params.type);
  if (params?.isActive !== undefined) searchParams.set('isActive', String(params.isActive));
  const qs = searchParams.toString();

  return useQuery<Referrer[]>({
    queryKey: ['referrers', params],
    queryFn: () => api.get(`/referrers${qs ? `?${qs}` : ''}`).then(r => r.data),
  });
}

// Custom stats hook (not standard CRUD)
export function useReferrerStats(id: string) {
  return useQuery<ReferrerStats>({
    queryKey: ['referrers', id, 'stats'],
    queryFn: () => api.get(`/referrers/${id}/stats`).then(r => r.data),
    enabled: !!id,
  });
}
